import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import reportingApi from '../../services/reportingApi'
import { T, page, usePoll, Empty, Chip, MatchRow, SectionTitle } from './ui'
import Scorecard from './Scorecard'

/**
 * Looking a result up, rather than watching one.
 *
 * The desk gets asked "how did Mumbai's mixed doubles go?" all day, so this is
 * a search rather than a feed: narrow by city, sport and day, or type a team's
 * name.
 */
const ResultsBrowser = () => {
  const [params, setParams] = useSearchParams()
  const [openMatch, setOpenMatch] = useState(null)
  const [term, setTerm] = useState(params.get('q') || '')

  const city = params.get('city') || ''
  const sport = params.get('sport') || ''
  const q = params.get('q') || ''

  const { data: context } = usePoll(() => reportingApi.context(), 0, [])
  const { data: sports } = usePoll(() => reportingApi.statSports(), 0, [])

  const { data, error } = usePoll(
    () => reportingApi.results({
      city: city || undefined,
      sport: sport || undefined,
      q: q || undefined,
      limit: 100,
    }),
    0,
    [city, sport, q],
  )

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params)
    value ? next.set(key, value) : next.delete(key)
    setParams(next)
  }

  const search = (e) => {
    e.preventDefault()
    setFilter('q', term.trim())
  }

  return (
    <div style={page}>
      <h2 style={{ margin: '0 0 4px', fontSize: 24 }}>Results</h2>
      <p style={{ margin: '0 0 20px', color: T.muted, fontSize: 14 }}>
        Every decided match. Open one for its full card.
      </p>

      <form onSubmit={search} style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search for a team or player"
          style={{
            flex: '1 1 260px', padding: '10px 13px', border: `1px solid #d9dee5`,
            borderRadius: 8, fontSize: 14,
          }}
        />
        <button type="submit" style={{
          padding: '10px 20px', background: T.accent, color: '#fff', border: 0,
          borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13.5,
        }}>Search</button>
        {q && (
          <button type="button" onClick={() => { setTerm(''); setFilter('q', '') }} style={{
            padding: '10px 16px', background: '#fff', border: `1px solid ${T.line}`,
            borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13.5, color: T.muted,
          }}>Clear</button>
        )}
      </form>

      <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
        <FilterRow label="City">
          <Chip on={!city} onClick={() => setFilter('city', '')}>All</Chip>
          {(context?.cities || []).map((c) => (
            <Chip key={c.city} on={city === c.city} onClick={() => setFilter('city', c.city)}>
              {c.city}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="Sport">
          <Chip on={!sport} onClick={() => setFilter('sport', '')}>All</Chip>
          {(sports?.data || []).map((s) => (
            <Chip key={s.sport} on={sport === s.sport} onClick={() => setFilter('sport', s.sport)}>
              {s.label}
            </Chip>
          ))}
        </FilterRow>
      </div>

      {error && <Empty>{error}</Empty>}
      {!data && !error && <Empty>Loading results…</Empty>}

      {data && (
        <>
          <SectionTitle aside={data.count === 100 ? 'showing the 100 most recent' : null}>
            {data.count} {data.count === 1 ? 'result' : 'results'}
          </SectionTitle>

          {data.count === 0 && <Empty>Nothing matches those filters.</Empty>}

          <div style={{ display: 'grid', gap: 8 }}>
            {data.data.map((m) => (
              <MatchRow key={m.id} m={{ ...m, category: [m.sport_label, m.city, m.category].filter(Boolean).join(' · ') }}
                        onOpen={setOpenMatch} />
            ))}
          </div>
        </>
      )}

      {openMatch && <Scorecard fixtureId={openMatch} onClose={() => setOpenMatch(null)} />}
    </div>
  )
}

const FilterRow = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
    <span style={{
      fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase',
      color: T.faint, fontWeight: 800, minWidth: 42,
    }}>{label}</span>
    {children}
  </div>
)

export default ResultsBrowser
